/**
 * Header row and example rows of every downloadable CSV template.
 *
 * One entry per upload that takes a CSV of SKU or price data. The header row
 * of each is checked against the server parser that reads the upload (see the
 * `csvTemplates*.test.ts` suites), so a template can never drift from what the
 * parser accepts. `frontend/src/constants/csvTemplates.ts` mirrors this file
 * byte for byte (CRA cannot import from `src/`); `csvTemplates.test.ts` pins
 * the two together.
 *
 * Plain data on purpose: no React, no i18n, no server imports, so the
 * frontend copy and the API tests can both load it.
 */
export interface CsvTemplateFixture {
  /** Name the browser saves the download under. */
  fileName: string;
  /** Header row, in column order. */
  headers: readonly string[];
  /** Two example rows, each the same length as `headers`. */
  rows: readonly (readonly string[])[];
}

/** SKU Overrides page. Parser: `skuOverrideService.parseCsv`. */
export const SKU_OVERRIDES_TEMPLATE: CsvTemplateFixture = {
  fileName: 'sku-overrides-template.csv',
  headers: ['upc', 'canonical_sku', 'custom_sku', 'custom_barcode', 'product_name'],
  rows: [
    ['012345678905', '', 'CAM-100-00001', '', 'Example camera matched by manufacturer UPC'],
    ['', 'ILCE-7M4', 'CAM-123-00045', 'CAM-123-00045', 'Example with no UPC matched by manufacturer SKU. custom_barcode is written to Shopify'],
  ],
};

/** Bulk price overrides dialog. Parser: `parseBulkOverrideCsv` / `BULK_OVERRIDE_HEADER_ALIASES`. */
export const BULK_PRICE_OVERRIDES_TEMPLATE: CsvTemplateFixture = {
  fileName: 'price-overrides-template.csv',
  headers: ['sku', 'upc', 'productId', 'brand', 'price', 'compareAt', 'title', 'ignoreRebate', 'ignoreSalePrice'],
  rows: [
    ['ILCE7M4/B', '', '', 'Sony', '2498.00', '', '', '', ''],
    ['', '027242922563', '', '', '2299.95', '', 'Alpha 7 IV body', 'yes', ''],
  ],
};

/** SKU Crosswalk import. Parser: `storeSkuMappingService.parseCsv` (`HEADER_ALIASES`). */
export const SKU_CROSSWALK_TEMPLATE: CsvTemplateFixture = {
  fileName: 'sku-crosswalk-template.csv',
  headers: ['upc', 'catalog_sku', 'store_sku', 'barcode', 'condition', 'integration_id', 'label', 'product_id'],
  rows: [
    ['013803324046', '', 'R5-NEW', '', 'new', '', 'EOS R5 body (new)', ''],
    ['', 'EOSR5', 'R5-OB', '2000000000017', 'open_box', '', 'EOS R5 body (open box)', ''],
  ],
};

/**
 * MAP / dealer pricing sheet: the store's MAP Pricing Data upload and the
 * vendor's private dealer sheet. This is the layout of the canonical
 * ("Motzu") sheet, `pricingIngestionService.parseMotzuSheet`, which both
 * uploads read when the file's header row matches it.
 */
export const MAP_SHEET_TEMPLATE: CsvTemplateFixture = {
  fileName: 'map-pricing-template.csv',
  headers: ['Model', 'Brand', 'MAP Price', 'MSRP', 'Instant Rebate', 'Trade Credit', 'Effective Date', 'End Date'],
  rows: [
    ['ILCE-7M4', 'Sony', '2498.00', '2498.00', '', '', '2026-11-01', ''],
    ['Z6 III', 'Nikon', '2499.95', '2499.95', '300.00', '', '2026-11-01', '2026-11-30'],
  ],
};

/**
 * Shopify Mapping tab, "Your taxonomy" upload. Parser: `merchantTaxonomyService.parseRegistryCsv`.
 * Subcategory Code is the bare code: Motzu prepends the department code (CAM + 111 is CAM-111).
 * Product Type and Shopify Category are optional; Shopify Category takes a GID, a bare id or
 * Shopify's full breadcrumb, so the second row shows the breadcrumb form.
 */
export const TAXONOMY_TEMPLATE: CsvTemplateFixture = {
  fileName: 'taxonomy-template.csv',
  headers: ['Department', 'Code', 'Category', 'Subcategory', 'Subcategory Code', 'Product Type', 'Shopify Category'],
  rows: [
    ['Cameras', 'CAM', 'Mirrorless', 'Full frame', '111', 'Mirrorless Cameras', 'gid://shopify/TaxonomyCategory/co-2-2-7'],
    [
      'Lenses', 'LEN', 'Prime', 'Wide angle', '210', 'Camera Lenses',
      'Cameras & Optics > Camera & Optic Accessories > Camera & Video Camera Lenses > Camera Lenses',
    ],
  ],
};

/**
 * Received serial numbers (flag `serial_capture`, Serial numbers page). Parser:
 * `parseReceivingCsv` in `storeSerialUnitService` (`HEADER_ALIASES`). Only
 * `serial` is required; `sku` / `upc` / `product_id` name the product through the
 * SKU crosswalk and the catalog, `received_at` is the arrival date.
 */
export const SERIAL_RECEIVING_TEMPLATE: CsvTemplateFixture = {
  fileName: 'serial-receiving-template.csv',
  headers: ['serial', 'sku', 'upc', 'product_id', 'received_at', 'brand'],
  rows: [
    ['0123456789AB', 'R5-NEW', '013803324046', '', '2026-10-01', ''],
    ['ZV1F00042', '', '027242926226', '', '', 'Sony'],
  ],
};

/** The same templates as one CSV string, `\n` between lines (what a parser test feeds in). */
export const templateToCsv = (template: CsvTemplateFixture): string =>
  [template.headers, ...template.rows].map((row) => row.join(',')).join('\n') + '\n';

/** The text the browser downloads: the same CSV with a UTF-8 byte-order mark and `\r\n` line ends, for Excel. */
export const templateAsDownloaded = (template: CsvTemplateFixture): string =>
  '\uFEFF' + templateToCsv(template).replace(/\n/g, '\r\n');
